import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, Cpu, Bell, ExternalLink, Zap,
} from "lucide-react";
import api from "@/lib/api";

/**
 * RightPanel — the "Today" context panel on the right of Home.
 *
 * Loads three sources in parallel on mount:
 *   • Today's calendar events
 *   • Focus session status
 *   • The 3 most-recent inbox items
 *
 * Also renders the device stats card when a device is connected.
 */
export default function RightPanel({ user, device, go }) {
  const [today, setToday] = useState({ connected: false, events: [] });
  const [focusStatus, setFocusStatus] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/calendar/today").catch(() => ({ data: { connected: false, events: [] } })),
      api.get("/integrations/focus/status").catch(() => ({ data: null })),
      api.get("/integrations/inbox").catch(() => ({ data: { items: [] } })),
    ]).then(([t, f, i]) => {
      setToday(t.data || { connected: false, events: [] });
      setFocusStatus(f.data);
      setInbox((i.data.items || []).slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  return (
    <aside
      className="hidden xl:flex w-[340px] shrink-0 h-full border-l border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/40 flex-col overflow-y-auto subtle-scroll"
      data-testid="right-panel"
    >
      <div className="p-5 space-y-5">
        {/* Today */}
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4" data-testid="rp-today">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold flex items-center gap-1.5">
              <CalendarDays size={12}/> Today
            </div>
            <button
              onClick={() => go("calendar")}
              className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold"
              data-testid="rp-today-manage"
            >
              {today.connected ? "Manage" : "Connect"}
            </button>
          </div>
          {loading && <div className="mt-3 text-sm text-[var(--rammy-text-muted)]">Reading your day…</div>}
          {!loading && !today.connected && (
            <div className="mt-3 text-sm text-[var(--rammy-text-sec)] leading-relaxed">
              Connect a calendar and Rammy will narrate your day here.
            </div>
          )}
          {!loading && today.connected && today.events.length === 0 && (
            <div className="mt-3 text-sm text-[var(--rammy-text-sec)]">Your day is quiet — enjoy the space.</div>
          )}
          {!loading && today.connected && today.events.length > 0 && (
            <div className="mt-3 space-y-2">
              {today.events.slice(0, 5).map((e, i) => (
                <div key={i} className="flex items-baseline gap-3 text-sm" data-testid={`rp-event-${i}`}>
                  <span className="w-14 shrink-0 text-[11px] font-mono text-[var(--rammy-cyan-dark)]">
                    {e.all_day ? "all day" : new Date(e.start).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}
                  </span>
                  <span className="flex-1 truncate">{e.title}</span>
                </div>
              ))}
              {today.events.length > 5 && (
                <div className="text-[11px] text-[var(--rammy-text-muted)]">+{today.events.length - 5} more</div>
              )}
            </div>
          )}
        </div>

        {/* Focus */}
        <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-4" data-testid="rp-focus">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Focus</div>
          {focusStatus?.active ? (
            <>
              <div className="mt-2 font-mono text-3xl tabular-nums">
                {formatRemaining(focusStatus.ends_at)}
              </div>
              {focusStatus.intention && (
                <div className="mt-1 text-xs text-white/70 italic truncate">&ldquo;{focusStatus.intention}&rdquo;</div>
              )}
              <button
                onClick={() => go("focus")}
                className="mt-3 text-[11px] uppercase tracking-widest text-[var(--rammy-cyan)] font-semibold"
                data-testid="rp-focus-open"
              >
                Open focus →
              </button>
            </>
          ) : (
            <>
              <div className="mt-2 text-sm text-white/80 leading-relaxed">
                Choose a quiet block. Rammy silences email while you work.
              </div>
              <button
                onClick={() => go("focus")}
                className="mt-3 w-full h-9 rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] text-xs font-semibold hover:bg-[var(--rammy-cyan)]/90 flex items-center justify-center gap-1.5"
                data-testid="rp-focus-start"
              >
                <Zap size={12}/> Start focus
              </button>
            </>
          )}
        </div>

        {/* Inbox teaser */}
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4" data-testid="rp-inbox">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold flex items-center gap-1.5">
              <Bell size={12}/> Inbox
            </div>
            <button
              onClick={() => go("inbox")}
              className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold"
              data-testid="rp-inbox-open"
            >
              Open
            </button>
          </div>
          {inbox.length === 0 ? (
            <div className="mt-3 text-sm text-[var(--rammy-text-sec)] leading-relaxed">
              No briefings yet. Turn on morning briefing in Connections.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {inbox.map(i => (
                <button
                  key={i.id}
                  onClick={() => go("inbox")}
                  className="w-full text-left"
                  data-testid={`rp-inbox-${i.id}`}
                >
                  <div className="flex items-center gap-2">
                    {i.unread && <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan)] shrink-0"/>}
                    <span className="text-sm font-medium truncate">{i.title}</span>
                  </div>
                  <div className="text-[11px] text-[var(--rammy-text-muted)] line-clamp-1">{i.body}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Device */}
        {device && (
          <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4" data-testid="rp-device">
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold flex items-center gap-1.5">
              <Cpu size={12}/> Your Rammy
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
              <div className="text-[var(--rammy-text-muted)]">Battery</div><div>{device.battery}%</div>
              <div className="text-[var(--rammy-text-muted)]">WiFi</div><div className="truncate">{device.wifi}</div>
              <div className="text-[var(--rammy-text-muted)]">Firmware</div><div className="font-mono text-xs">{device.firmware}</div>
            </div>
            <Link to="/device" data-testid="rp-device-open">
              <button className="mt-3 w-full h-9 rounded-full border border-[var(--rammy-border)] text-xs font-semibold hover:border-[var(--rammy-cyan)] flex items-center justify-center gap-1.5">
                Open robot face <ExternalLink size={11}/>
              </button>
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}

function formatRemaining(endsAt) {
  if (!endsAt) return "0:00";
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "0:00";
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}
